import { useState } from 'react';
import {
  RefetchOptions,
  RefetchQueryFilters,
  QueryObserverResult,
} from 'react-query';
import { Button, Icon, StackView, Text } from '@cronos-labs/tectonic-ui-kit';
import ArrowUpSVG from '@public/assets/images/arrow-up.svg';
import ArrowDownSVG from '@public/assets/images/arrow-down.svg';
import clsx from 'clsx';
import { useRouter } from 'next/router';

import useVaultDeposits from '@hooks/useVaultDeposits';
import useIsMobile from '@hooks/useIsMobile';
import Card from '@components/Card/Card';
import TransactionModalsProvider, {
  TransactionModalsContext,
} from '@providers/TransactionModalsProvider/TransactionModalsProvider';

import { useGetBoostingMultiplier } from './XTonicBoostingHooks';

interface XTonicBeforeBoostingProps {
  refetchBoostedSupplyCollections: <TPageData>(
    options?: (RefetchOptions & RefetchQueryFilters<TPageData>) | undefined
  ) => Promise<QueryObserverResult<string[], unknown>>;
  refetchBoostedBorrowCollections: <TPageData>(
    options?: (RefetchOptions & RefetchQueryFilters<TPageData>) | undefined
  ) => Promise<QueryObserverResult<string[], unknown>>;
}

const XTonicBeforeBoosting = ({
  refetchBoostedSupplyCollections,
  refetchBoostedBorrowCollections,
}: XTonicBeforeBoostingProps) => {
  const [isCollapse, setCollapse] = useState<boolean>(true);
  const [startButtonClicked, setButtonClicked] = useState<boolean>(false);

  const isMobile = useIsMobile();
  const { data } = useVaultDeposits();

  const { push } = useRouter();

  const { data: multiplier } = useGetBoostingMultiplier();

  const xTonicLocked = data?.some((deposit) => deposit.amount.gt(0));

  const handleLock = () => push('/vaults');

  return (
    <TransactionModalsProvider
      onTxSuccess={(): void => {
        refetchBoostedSupplyCollections();
        refetchBoostedBorrowCollections();
      }}
    >
      <TransactionModalsContext.Consumer>
        {({ onOpenModal }) => (
          <div className="mt-2 bg-gradient-to-r from-highlightBorderColorFrom via-rainbowBlue to-highlightBorderColorTo p-[1px] mobile:rounded-[5px]">
            <Card padding="small" className="mobile:rounded-[5px]">
              <div className="flex w-full justify-between bg-surface mobile:flex-col">
                <StackView direction="vertical" className="w-11/12">
                  <div className="flex items-center">
                    {' '}
                    <Icon.Boost className="text-yellowPrimary" />
                    <Text
                      className="ml-2 text-yellowPrimary"
                      variant="heading3"
                    >
                      {xTonicLocked
                        ? 'Multiplier Unlocked'
                        : 'Multiply your rewards'}
                    </Text>
                  </div>
                  <Text variant="default" className="mt-1">
                    Earn higher interest or pay lower borrowing fees by locking
                    xTONIC in a vault.
                  </Text>
                  <a
                    className="mt-3 !bg-surface text-yellow"
                    href="https://tectonic.gitbook.io/docs/protocol/tonic-rewards-boost"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    Learn more
                  </a>
                </StackView>

                {!startButtonClicked ? (
                  <Button
                    size="medium"
                    className="mt-1 h-[40px] mobile:hidden"
                    onClick={() => {
                      setCollapse(false);
                      setButtonClicked(true);
                    }}
                  >
                    {xTonicLocked ? 'Activate' : 'Start'}
                  </Button>
                ) : (
                  <div
                    onClick={() => setCollapse((prev) => !prev)}
                    className="cursor-pointer mobile:hidden"
                  >
                    {isCollapse ? (
                      <ArrowDownSVG fill={'yellow'} stroke={'yellow'} />
                    ) : (
                      <ArrowUpSVG fill={'yellow'} stroke={'yellow'} />
                    )}
                  </div>
                )}
              </div>

              {xTonicLocked && multiplier && (
                <div className="flex items-center">
                  <Text>
                    Boost Multiplier {xTonicLocked ? 'available' : 'applied'}:
                  </Text>
                  <Text className="ml-1 text-vaultChart12Months">
                    {multiplier ?? ''}x
                  </Text>
                </div>
              )}

              {isMobile && (
                <div className="relative">
                  <div className=" flex items-baseline justify-between">
                    <div className="flex items-baseline">
                      <div
                        className={clsx(
                          'mt-1 h-3 w-3 rounded-full text-center leading-6 text-darkerBlue',
                          {
                            'bg-completed': xTonicLocked,
                            'bg-semiTransparent': !xTonicLocked,
                          }
                        )}
                      >
                        {!xTonicLocked ? 1 : ''}
                      </div>
                      <Text className="ml-1">Lock xTonic in a vault</Text>
                      <div
                        className={clsx({
                          hidden: !xTonicLocked,
                        })}
                      >
                        xTONIC locked
                      </div>
                    </div>
                    <div
                      className="cursor-pointer font-medium text-yellow"
                      onClick={handleLock}
                    >
                      Lock
                    </div>
                  </div>
                  <Card
                    className="absolute ml-4 mt-1.5 !w-11/12 border-0 bg-blueElevatedSurface p-1"
                    padding="small"
                  >
                    <div className="flex items-center justify-between">
                      <Icon.Increment className="text-rainbowRed" />
                      <Text className="ml-0.5">
                        Stake NFT for higher APY (Optional)
                      </Text>
                      <div
                        className={clsx(
                          'cursor-pointer font-medium',
                          xTonicLocked ? 'text-yellow' : 'text-greyBorder'
                        )}
                      >
                        Stake
                      </div>
                    </div>
                  </Card>
                  <div className="ml-1.5 mt-1 inline-block h-[85px] min-h-[1em] w-[1px] bg-greyBorder" />
                  <div className="flex items-baseline justify-between">
                    <div className="flex items-baseline">
                      <div
                        className={clsx(
                          'mt-1 h-3 w-3 rounded-full text-center leading-6 text-darkerBlue',
                          xTonicLocked ? 'bg-semiTransparent' : 'bg-greyBorder'
                        )}
                      >
                        2
                      </div>
                      <Text className="ml-1">Boost up to 2 markets</Text>
                    </div>
                    <div
                      className={clsx(
                        'cursor-pointer font-medium',
                        xTonicLocked ? 'text-yellow' : 'text-greyBorder'
                      )}
                      onClick={(e) => {
                        e.preventDefault();
                        onOpenModal(null, 'boost');
                      }}
                    >
                      Boost
                    </div>
                  </div>
                </div>
              )}

              {!isMobile && !isCollapse && (
                <>
                  <div className="m-auto flex w-2/4">
                    <div
                      className={clsx(
                        'h-3 w-3 rounded-full bg-semiTransparent text-center leading-6 text-darkerBlue',
                        { 'bg-completed': xTonicLocked }
                      )}
                    >
                      {!xTonicLocked ? 1 : ''}
                    </div>
                    <div className="h-px my-1.5 ml-0.5 w-full border-1 border-greyBorder bg-greyBorder" />
                    <div
                      className={clsx(
                        'ml-1 h-3 w-3 rounded-full text-center leading-6 text-darkerBlue',
                        xTonicLocked ? 'bg-semiTransparent' : 'bg-greyBorder'
                      )}
                    >
                      2
                    </div>
                  </div>
                  <div className="mt-2 flex justify-around">
                    <div className="flex flex-col items-center gap-y-1">
                      <div className="flex flex-row">
                        <Text>Lock xTonic in a vault</Text>
                        <div
                          className="ml-2 cursor-pointer font-medium text-yellow"
                          onClick={handleLock}
                        >
                          Lock
                        </div>
                      </div>
                      <div
                        className={clsx({
                          hidden: !xTonicLocked,
                        })}
                      >
                        xTONIC locked
                      </div>
                    </div>

                    <div className="flex">
                      <Text>Boost up to 2 markets</Text>
                      <div
                        className={clsx(
                          'ml-2 cursor-pointer font-medium',
                          xTonicLocked ? 'text-yellow' : 'text-greyBorder'
                        )}
                        onClick={(e) => {
                          e.preventDefault();
                          onOpenModal(null, 'boost');
                        }}
                      >
                        Boost
                      </div>
                    </div>
                  </div>
                  <Card
                    className="ml-10 mt-1.5 !w-1/3 border-0 bg-blueElevatedSurface p-1"
                    padding="small"
                  >
                    <div className="flex items-center justify-between">
                      <Icon.Increment className="text-rainbowRed" />
                      <Text className="ml-0.5">
                        Stake NFT for higher APY (Optional)
                      </Text>
                      <div
                        className={clsx(
                          'cursor-pointer font-medium text-yellow',
                          xTonicLocked ? 'text-yellow' : 'text-greyBorder'
                        )}
                        onClick={handleLock}
                      >
                        Stake
                      </div>
                    </div>
                  </Card>
                </>
              )}
            </Card>
          </div>
        )}
      </TransactionModalsContext.Consumer>
    </TransactionModalsProvider>
  );
};

export default XTonicBeforeBoosting;
